/**
 * One state machine per pending chip: ask the API for a presigned PUT, send
 * the bytes with XHR (fetch has no upload progress), report progress, and end
 * in done or failed. Removing an uploading chip aborts its request; the
 * pending row and any bytes are left for the hourly orphan sweep.
 *
 * The files and the XHRs are module maps and the chips live in `chatChips`,
 * because switching channels unmounts the channel view and must not cancel an
 * upload in flight: the XHR callbacks keep patching the stored chips.
 */

import { useCallback } from 'react';

import { requestChatAttachmentUpload } from '../../services/api';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

export type PendingChipState = 'uploading' | 'done' | 'failed';

export interface PendingChip {
  localId: string;
  fileName: string;
  sizeBytes: number;
  thumbnailUrl?: string;
  state: PendingChipState;
  progress: number;
  attachmentId?: string;
}

const files = new Map<string, File>();
const requests = new Map<string, XMLHttpRequest>();

// One stable empty array, so a channel with no chips does not re-render.
const EMPTY_CHIPS: PendingChip[] = [];

/** A discarded chip's image thumbnail would otherwise hold its blob until the
 * page closes. */
const revokeThumbnail = (chip: PendingChip): void => {
  if (chip.thumbnailUrl) URL.revokeObjectURL(chip.thumbnailUrl);
};

export const useAttachmentUpload = (
  channelId: string,
  onNotice?: (message: string) => void
) => {
  const chips = useStore(state => state.chatChips[channelId] ?? EMPTY_CHIPS);

  const setChips = useCallback(
    (next: (current: PendingChip[]) => PendingChip[]) =>
      useStore.getState().setChips(channelId, next),
    [channelId]
  );

  const patch = useCallback(
    (localId: string, next: Partial<PendingChip>) =>
      setChips(current =>
        current.map(chip =>
          chip.localId === localId ? { ...chip, ...next } : chip
        )
      ),
    [setChips]
  );

  const upload = useCallback(
    async (localId: string) => {
      const file = files.get(localId);
      if (!file) return;
      patch(localId, { state: 'uploading', progress: 0 });
      try {
        const { data } = await requestChatAttachmentUpload({
          path: { channel_id: channelId },
          body: { file_name: file.name, size_bytes: file.size },
          throwOnError: true,
        });
        // The chip was removed while the URL request ran: send nothing.
        if (!files.has(localId)) return;
        const request = new XMLHttpRequest();
        requests.set(localId, request);
        request.open('PUT', data.upload_url);
        // Signed headers. The browser sets Content-Length itself.
        request.setRequestHeader('Content-Type', data.content_type);
        // Create-only: the URL cannot overwrite the object once it lands.
        request.setRequestHeader('If-None-Match', '*');
        request.upload.onprogress = event => {
          if (event.lengthComputable) {
            patch(localId, {
              progress: Math.round((event.loaded / event.total) * 100),
            });
          }
        };
        request.onload = () => {
          requests.delete(localId);
          patch(
            localId,
            request.status >= 200 && request.status < 300
              ? {
                  state: 'done',
                  progress: 100,
                  attachmentId: data.attachment_id,
                }
              : { state: 'failed' }
          );
        };
        request.onerror = () => {
          requests.delete(localId);
          patch(localId, { state: 'failed' });
        };
        request.send(file);
      } catch (error) {
        console.error('Attachment upload could not start', error);
        // A 422 carries the server's limit text; anything else leaves only the
        // chip's own "Upload failed." with Try again.
        const message = getBusinessErrorMessage(error);
        if (message) onNotice?.(message);
        patch(localId, { state: 'failed' });
      }
    },
    [channelId, patch, onNotice]
  );

  const addFiles = useCallback(
    (incoming: File[]) => {
      const added: PendingChip[] = incoming.map(file => {
        const localId = crypto.randomUUID();
        files.set(localId, file);
        return {
          localId,
          fileName: file.name,
          sizeBytes: file.size,
          thumbnailUrl: file.type.startsWith('image/')
            ? URL.createObjectURL(file)
            : undefined,
          state: 'uploading' as const,
          progress: 0,
        };
      });
      setChips(current => [...current, ...added]);
      added.forEach(chip => void upload(chip.localId));
    },
    [setChips, upload]
  );

  const remove = useCallback(
    (localId: string) => {
      requests.get(localId)?.abort();
      requests.delete(localId);
      files.delete(localId);
      setChips(current =>
        current.filter(chip => {
          if (chip.localId !== localId) return true;
          revokeThumbnail(chip);
          return false;
        })
      );
    },
    [setChips]
  );

  const retry = useCallback(
    (localId: string) => void upload(localId),
    [upload]
  );

  /** Drop only the chips a send submitted; a chip added meanwhile stays. */
  const clearSubmitted = useCallback(
    (attachmentIds: string[]) => {
      const sent = new Set(attachmentIds);
      setChips(current =>
        current.filter(chip => {
          const submitted = !!chip.attachmentId && sent.has(chip.attachmentId);
          if (submitted) {
            files.delete(chip.localId);
            revokeThumbnail(chip);
          }
          return !submitted;
        })
      );
    },
    [setChips]
  );

  return {
    chips,
    addFiles,
    remove,
    retry,
    clearSubmitted,
    uploading: chips.some(chip => chip.state === 'uploading'),
    doneIds: chips.flatMap(chip =>
      chip.state === 'done' && chip.attachmentId ? [chip.attachmentId] : []
    ),
  };
};
